import React, { useState, useEffect } from 'react';
import { observer, useLocalObservable } from 'mobx-react-lite';
import { runInAction } from 'mobx';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    MenuItem,
    Tab,
    Tabs,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
    OrganizationEvaTemplateDecisionOptionDto,
    OrganizationEvaTemplateDto,
    OrganizationEvaTemplateImpactOnTargetDto,
    OrganizationEvaTemplatePerspectiveDto,
} from '../../types/evaTemplate';
import { TextFieldEvent } from '../../types/events';
import { EvaNestableItem } from '../../types/nestable';
import ShvkTextField from '../../styled/ShvkTextField';
import DangerButton from '../../styled/DangerButton';
import ShvkButton from '../../styled/ShvkButton';
import EvaStructure from './EvaStructure';
import ShvkTabPanel from '../ShvkTabPanel';
import useStore from '../../store/storeContext';

const PREFIX = 'EvaTemplateDialog';

const classes = {
    paper: `${PREFIX}-paper`,
    dialogTitle: `${PREFIX}-dialogTitle`,
};

const StyledDialog = styled(Dialog)(() => ({
    [`& .${classes.paper}`]: {
        height: '90%',
    },

    [`& .${classes.dialogTitle}`]: {
        paddingBottom: 0,
    },
}));

interface Props {
    open: boolean;
    onClose(): void;
    evaTemplate: OrganizationEvaTemplateDto | null;
}

interface State {
    activeTab: TabName;
    name: string;
    description: string;
    organizationId: string;
}

interface StoreState {
    evaStructure: EvaNestableItem[];
}

type TabName = 'DESCRIPTION' | 'STRUCTURE';
type EvaTemplateDialogField = 'name' | 'description' | 'organizationId';

function EvaTemplateDialog(props: Props) {
    const context = useStore();
    const { organization, dialog, localization, snackbar } = context;

    const [state, setState] = useState<State>({
        activeTab: 'DESCRIPTION',
        name: '',
        description: '',
        organizationId: '',
    });

    const localStore = useLocalObservable<StoreState>(() => ({
        evaStructure: [],
        removedEvaItems: [],
    }));

    useEffect(() => {
        props.open && init();
        return () => {
            setState((state) => ({
                ...state,
                activeTab: 'DESCRIPTION',
                name: '',
                description: '',
                organizationId: '',
            }));
        };
    }, [props.open]);

    function init(): void {
        const { evaTemplate } = props;
        setState((state) => ({
            ...state,
            activeTab: 'DESCRIPTION',
            name: evaTemplate?.title || '',
            description: evaTemplate?.description || '',
            organizationId: evaTemplate?.organization.id?.toString() || '',
        }));
        runInAction(() => {
            localStore.evaStructure = [];
        });
    }

    async function save(): Promise<void> {
        try {
            const template: OrganizationEvaTemplateDto = {
                id: props.evaTemplate?.id || null,
                description: state.description,
                title: state.name,
                organization: organization.adminOrganizations.find((org) => org.id === Number(state.organizationId))!,
                decisionOptions: localStore.evaStructure.map((decisionOption) => {
                    return {
                        id: null,
                        description: decisionOption.description,
                        title: decisionOption.text,
                        organizationEvaTemplateId: props.evaTemplate?.id || null,
                        perspectives: decisionOption.children.map((perspective) => {
                            return {
                                id: null,
                                title: perspective.text,
                                orderNumber: 0,
                                organizationEvaTemplateDecisionOptionId: null,
                                organizationEvaTemplateImpactOnTargets: perspective.children.map((impactOnTarget) => {
                                    return {
                                        id: null,
                                        description: impactOnTarget.description,
                                        orderNumber: 0,
                                        organizationEvaTemplatePerspectiveId: null,
                                        title: impactOnTarget.text,
                                    } as OrganizationEvaTemplateImpactOnTargetDto;
                                }),
                            } as OrganizationEvaTemplatePerspectiveDto;
                        }),
                    } as OrganizationEvaTemplateDecisionOptionDto;
                }),
            };

            if (template.id) {
                //Update existing eva template
                await context.evaTemplate.updateEvaTemplate(template);
            } else {
                //Create new eva template
                await context.evaTemplate.createEvaTemplate(template);
            }
            snackbar.showSuccess();
        } catch (error) {
            snackbar.showError(error.data?.code);
        } finally {
            props.onClose();
        }
    }

    async function removeTemplate(): Promise<void> {
        try {
            const confirmation = await dialog.getConfirmation(
                undefined,
                translate('GENERAL_CONFIRMATION_MESSAGE'),
                true,
            );
            if (props.evaTemplate && props.evaTemplate.id && confirmation) {
                await context.evaTemplate.removeEvaTemplate(props.evaTemplate.id);
                snackbar.showSuccess();
                props.onClose();
            }
        } catch (error) {
            snackbar.showError(error.data?.code);
        }
    }

    function setEvaStructure(list: EvaNestableItem[]): void {
        runInAction(() => (localStore.evaStructure = list));
    }

    function structureTab(): JSX.Element {
        return (
            <EvaStructure
                decisionOptions={props.evaTemplate?.decisionOptions}
                list={localStore.evaStructure}
                setList={setEvaStructure}
            />
        );
    }

    const handleTabChange = (_event: React.ChangeEvent<unknown>, newIndex: TabName): void => {
        setState((state) => ({ ...state, activeTab: newIndex }));
    };

    const handleChange = (event: TextFieldEvent, field: EvaTemplateDialogField): void => {
        setState((state) => ({ ...state, [field]: event.target.value }));
    };

    function descriptionTab(): JSX.Element {
        const { translate } = localization;
        const { evaTemplate } = props;
        return (
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <ShvkTextField
                        required
                        label={translate('ORGANIZATION_EVA_TEMPLATE_NAME')}
                        value={state.name}
                        variant="outlined"
                        size="small"
                        fullWidth
                        onChange={(event): void => handleChange(event, 'name')}
                    />
                </Grid>
                <Grid item xs={12}>
                    <ShvkTextField
                        multiline
                        rows={6}
                        label={translate('ORGANIZATION_EVA_TEMPLATE_DESCRIPTION')}
                        value={state.description}
                        variant="outlined"
                        size="small"
                        fullWidth
                        onChange={(event): void => handleChange(event, 'description')}
                    />
                </Grid>
                <Grid item xs={12}>
                    <ShvkTextField
                        required
                        select
                        label={translate('ORGANIZATION')}
                        value={state.organizationId}
                        variant="outlined"
                        size="small"
                        fullWidth
                        onChange={(event): void => handleChange(event, 'organizationId')}
                        disabled={Boolean(evaTemplate)}
                        SelectProps={{ MenuProps: { sx: { maxHeight: 300 } } }}
                    >
                        {organization.adminOrganizations
                            .slice()
                            .sort((a, b) => {
                                const aName =
                                    a.name[localization.locale] || a.name.fi_FI || a.name.sv_SE || a.name.en_GB || '';
                                const bName =
                                    b.name[localization.locale] || b.name.fi_FI || b.name.sv_SE || b.name.en_GB || '';

                                if (!aName) return 1;
                                if (!bName) return -1;

                                return aName > bName ? 1 : -1;
                            })
                            .map((organization) => (
                                <MenuItem key={organization.id} value={organization.id!}>
                                    {organization.name[localization.locale] ||
                                        organization.name.fi_FI ||
                                        organization.name.sv_SE ||
                                        organization.name.en_GB ||
                                        ''}
                                </MenuItem>
                            ))}
                    </ShvkTextField>
                </Grid>
            </Grid>
        );
    }

    function getIsSaveButtonDisabled(): boolean {
        return !state.organizationId || !state.name || localStore.evaStructure.length === 0;
    }

    const isSaveButtonDisabled = getIsSaveButtonDisabled();
    const translate = localization.translate;

    return (
        <StyledDialog
            open={props.open}
            onClose={props.onClose}
            aria-labelledby="eva-template-dialog-title"
            maxWidth={'md'}
            fullWidth
            classes={{
                paper: classes.paper,
            }}
        >
            <DialogTitle classes={{ root: classes.dialogTitle }}>
                <span id="eva-template-dialog-title">{translate('ORGANIZATION_EVA_TEMPLATE')}</span>
                <Tabs
                    value={state.activeTab}
                    indicatorColor="primary"
                    textColor="primary"
                    onChange={handleTabChange}
                    variant="fullWidth"
                    aria-labelledby="eva-template-dialog-title"
                >
                    <Tab label={translate('ORGANIZATION_EVA_TEMPLATE_DESCRIPTION')} value="DESCRIPTION" />
                    <Tab label={translate('EVA_OPTIONS_PERSPECTIVES_TARGETS')} value="STRUCTURE" />
                </Tabs>
            </DialogTitle>
            <DialogContent dividers>
                <ShvkTabPanel value={'DESCRIPTION'} activePanel={state.activeTab}>
                    {descriptionTab()}
                </ShvkTabPanel>
                <ShvkTabPanel value={'STRUCTURE'} activePanel={state.activeTab}>
                    {structureTab()}
                </ShvkTabPanel>
            </DialogContent>
            <DialogActions>
                <Button onClick={props.onClose} variant="contained">
                    {translate('CLOSE')}
                </Button>
                {props.evaTemplate && (
                    <DangerButton onClick={removeTemplate} variant="contained">
                        {translate('REMOVE')}
                    </DangerButton>
                )}
                <Box flexGrow={1} />
                <ShvkButton onClick={save} disabled={isSaveButtonDisabled}>
                    {translate('SAVE')}
                </ShvkButton>
            </DialogActions>
        </StyledDialog>
    );
}

export default observer(EvaTemplateDialog);
